import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Separator } from "../../ui/separator";
import { TrendingUp, DollarSign, Percent, Users, Info, Sparkles, Zap, Target } from "lucide-react";
import './EditableMetricsDialog.css';

const EditableMetricsDialog = ({ open, onOpenChange, efficiency, effectiveness, onSave }) => {
  const [totalFTEs, setTotalFTEs] = useState(efficiency.totalFTEs);
  const [avgSalary, setAvgSalary] = useState(efficiency.avgSalary);
  const [overallAutomation, setOverallAutomation] = useState(efficiency.overallAutomation);
  const [totalSpend, setTotalSpend] = useState(effectiveness.totalSpend);
  const [spendUnderManagement, setSpendUnderManagement] = useState(effectiveness.spendUnderManagement);
  const [minSavingsRate, setMinSavingsRate] = useState(effectiveness.minSavingsRate);
  const [maxSavingsRate, setMaxSavingsRate] = useState(effectiveness.maxSavingsRate);

  useEffect(() => {
    setTotalFTEs(efficiency.totalFTEs);
    setAvgSalary(efficiency.avgSalary);
    setOverallAutomation(efficiency.overallAutomation);
  }, [efficiency]);

  useEffect(() => {
    setTotalSpend(effectiveness.totalSpend);
    setSpendUnderManagement(effectiveness.spendUnderManagement);
    setMinSavingsRate(effectiveness.minSavingsRate);
    setMaxSavingsRate(effectiveness.maxSavingsRate);
  }, [effectiveness]);

  const handleSave = () => {
    onSave(
      {
        totalFTEs,
        avgSalary,
        overallAutomation,
      },
      {
        totalSpend,
        spendUnderManagement,
        minSavingsRate,
        maxSavingsRate,
      }
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="savings-dialog">
        <DialogHeader>
          <div className="savings-icon-wrapper">
            <Sparkles className="savings-header-icon" />
          </div>
          <DialogTitle className="savings-dialog-title">Edit Calculation Inputs</DialogTitle>
          <DialogDescription className="savings-dialog-desc">
            Fine-tune parameters to recalculate efficiency and effectiveness savings
          </DialogDescription>
        </DialogHeader>

        <div className="savings-main-content">
          {/* Efficiency Section */}
          <div className="savings-section">
            <div className="savings-section-header">
              <div className="savings-section-icon savings-icon-blue">
                <TrendingUp size={20} />
              </div>
              <div className="savings-section-title-group">
                <h4 className="savings-section-title">Efficiency Parameters</h4>
                <p className="savings-section-subtitle">Workforce automation metrics</p>
              </div>
            </div>

            <div className="savings-grid">
              <div className="savings-field">
                <Label htmlFor="total-ftes" className="savings-label">
                  <Users className="savings-label-icon" />
                  Total FTEs
                </Label>
                <Input
                  id="total-ftes"
                  type="number"
                  value={totalFTEs}
                  onChange={(e) => setTotalFTEs(Number(e.target.value))}
                  className="savings-input"
                />
              </div>

              <div className="savings-field">
                <Label htmlFor="avg-salary" className="savings-label">
                  <DollarSign className="savings-label-icon" />
                  Average Salary
                </Label>
                <div className="savings-input-wrapper">
                  <span className="savings-prefix">$</span>
                  <Input
                    id="avg-salary"
                    type="number"
                    value={avgSalary}
                    onChange={(e) => setAvgSalary(Number(e.target.value))}
                    className="savings-input savings-input-prefix"
                  />
                </div>
              </div>

              <div className="savings-field savings-field-full">
                <Label htmlFor="automation" className="savings-label">
                  <Percent className="savings-label-icon" />
                  Automation Potential
                </Label>
                <div className="savings-input-wrapper">
                  <Input
                    id="automation"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={overallAutomation}
                    onChange={(e) => setOverallAutomation(Number(e.target.value))}
                    className="savings-input"
                  />
                  <span className="savings-suffix">%</span>
                </div>
              </div>
            </div>

            <div className="savings-info-box savings-info-blue">
              <Info className="savings-info-icon" size={16} />
              <div className="savings-info-content">
                <p className="savings-info-formula">Σ(FTE × Salary × Automation%)</p>
                <p className="savings-info-note">Calculated at process level and aggregated</p>
              </div>
            </div>
          </div>

          <Separator className="savings-separator" />

          {/* Effectiveness Section */}
          <div className="savings-section">
            <div className="savings-section-header">
              <div className="savings-section-icon savings-icon-purple">
                <DollarSign size={20} />
              </div>
              <div className="savings-section-title-group">
                <h4 className="savings-section-title">Effectiveness Parameters</h4>
                <p className="savings-section-subtitle">Spend optimization metrics</p>
              </div>
            </div>

            <div className="savings-grid">
              <div className="savings-field savings-field-full">
                <Label htmlFor="total-spend" className="savings-label">
                  <DollarSign className="savings-label-icon" />
                  Total Spend
                </Label>
                <div className="savings-input-wrapper">
                  <span className="savings-prefix">$</span>
                  <Input
                    id="total-spend"
                    type="number"
                    value={totalSpend}
                    onChange={(e) => setTotalSpend(Number(e.target.value))}
                    className="savings-input savings-input-prefix"
                  />
                </div>
              </div>

              <div className="savings-field savings-field-full">
                <Label htmlFor="spend-mgmt" className="savings-label">
                  <Percent className="savings-label-icon" />
                  Spend Under Management
                </Label>
                <div className="savings-input-wrapper">
                  <Input
                    id="spend-mgmt"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={spendUnderManagement}
                    onChange={(e) => setSpendUnderManagement(Number(e.target.value))}
                    className="savings-input"
                  />
                  <span className="savings-suffix">%</span>
                </div>
              </div>

              <div className="savings-field">
                <Label htmlFor="min-savings" className="savings-label">
                  <Target className="savings-label-icon" />
                  Min Savings Rate
                </Label>
                <div className="savings-input-wrapper">
                  <Input
                    id="min-savings"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={minSavingsRate}
                    onChange={(e) => setMinSavingsRate(Number(e.target.value))}
                    className="savings-input"
                  />
                  <span className="savings-suffix">%</span>
                </div>
              </div>

              <div className="savings-field">
                <Label htmlFor="max-savings" className="savings-label">
                  <Zap className="savings-label-icon" />
                  Max Savings Rate
                </Label>
                <div className="savings-input-wrapper">
                  <Input
                    id="max-savings"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={maxSavingsRate}
                    onChange={(e) => setMaxSavingsRate(Number(e.target.value))}
                    className="savings-input"
                  />
                  <span className="savings-suffix">%</span>
                </div>
              </div>
            </div>

            <div className="savings-info-box savings-info-purple">
              <Info className="savings-info-icon" size={16} />
              <div className="savings-info-content">
                <p className="savings-info-formula">Total Spend × Unmanaged% × Uplift Rate × Savings Rate</p>
                <p className="savings-info-note">Uplift Rate = Overall Automation × 50%</p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="savings-dialog-footer">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="savings-btn-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} className="savings-btn-save">
            <Sparkles size={16} />
            Save & Recalculate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditableMetricsDialog;